import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useAppSelector } from '../../app/store';
import type { AuthorizationSnapshot } from '../api/types';
import type { ActionName } from './permissionMatrix';

type Permissions = { can: (scopeBase: string, action: ActionName) => boolean; hasScope: (scope: string) => boolean };
const PermissionContext = createContext<Permissions>({ can: () => false, hasScope: () => false });

const legacyAreas: Record<string, string[]> = {
  'platform.memberships': ['platform.memberships', 'platform.techpos.users'],
  'platform.roles': ['platform.techpos.roles', 'platform.techpos.role-actions'],
  'platform.notification-configuration': ['platform.techpos.notification-configuration'],
  'platform.notification-rules': ['platform.techpos.notification-user-categories'],
};

export function permissionsFor(snapshot: AuthorizationSnapshot): Permissions {
  const scopes = new Set([...(snapshot.openScopes ?? []), ...(snapshot.administrativeScopes ?? []), ...(snapshot.readScopes ?? [])]);
  for (const resource of snapshot.resources) {
    if (resource.isSuperAdminOnly && snapshot.isSuperAdmin !== true) continue;
    if (resource.actions && resource.scopeBase)
      for (const action of resource.actions) scopes.add(`${resource.scopeBase}.${action}`);
  }
  if (snapshot.isSuperAdmin !== true) {
    scopes.delete('platform.organization.provision');
    scopes.delete('platform.resources.manage');
  }
  return {
    hasScope: scope => scopes.has(scope),
    can: (base, action) => {
      if (scopes.has(`${base}.${action}`) || scopes.has(`${base}.execute`)) return true;
      // An action-aware snapshot never interprets compatibility .write as full access.
      if (snapshot.permissionVersion === 2) return false;
      return snapshot.resources.some(r => (!r.isSuperAdminOnly || snapshot.isSuperAdmin === true) && (r.scopeBase === base || r.code === base || legacyAreas[base]?.includes(r.code)) &&
        (action === 'read' ? r.canRead : r.canWrite));
    },
  };
}

export function PermissionProvider({ children }: { children: ReactNode }) {
  const snapshot = useAppSelector(s => s.session.snapshot);
  const permissions = useMemo(() => permissionsFor(snapshot), [snapshot]);
  return <PermissionContext.Provider value={permissions}>{children}</PermissionContext.Provider>;
}

// Context survives the nested legacy Redux provider used by ported components.
export function usePermissions() { return useContext(PermissionContext); }
export function useCan(scopeBase: string, action: ActionName) { return usePermissions().can(scopeBase, action); }
